import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { OpenModal, closeModal, setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import Modal from "../../Services/Modal";
import moment from "moment";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";
import { Scanner } from "@yudiel/react-qr-scanner";
import DivisionRegistrationEventView from "./DivisionRegistrationEventView";

const emptyDivisionStat = () => ({
    present: 0,
    late: 0,
    absent: 0,
    out_of_station: 0,
    intl_khidmat: 0,
    leave: 0,
    total: 0,
});

const getAttendanceBucket = (responseStatus) => {
    const status = parseInt(responseStatus, 10) || 0;
    if (status === 1) return "present";
    if (status === 2) return "late";
    if (status === 4) return "out_of_station";
    if (status === 5) return "intl_khidmat";
    if (status === 6) return "leave";
    // status 0 / no response and status 3 both count as absent
    return "absent";
};

const getMemberDisplayName = (item) => (`${item.user_title_prefix || ""} ${item.first_name || ""} Bhai ${item.user_last_name || ""}`).trim();

const matchesMemberSearch = (item, term) => {
    const q = String(term || "").trim().toLowerCase();
    if (!q) return true;
    const name = getMemberDisplayName(item).toLowerCase();
    const its = String(item.user_its || "").toLowerCase();
    const division = String(item.division_name || "").toLowerCase();
    return name.includes(q) || its.includes(q) || division.includes(q);
};

const ViewEvent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userData, accessToken } = useSelector((state) => state.user);
    const [eventData, setEventData] = useState({});
    const [qrPopup, openQrPopup] = useState(false);
    const [qrScannerPopup, openQrScannerPopup] = useState(false);
    const [membersList, setMemberList] = useState([]);
    const [alreadyMarked, setAlreadyMarked] = useState(false);
    const [markedTime, setmarkedTime] = useState('');
    const [FullMembersList, setFullMembersList] = useState([]);
    const [memberSearch, setMemberSearch] = useState("");

    const filteredMembersList = useMemo(
        () => membersList.filter((item) => matchesMemberSearch(item, memberSearch)),
        [membersList, memberSearch]
    );

    const isMultiOrAllDivisionEvent = useMemo(() => {
        const raw = String(eventData?.event_user_div ?? "0");
        const divIds = raw.split(",").map((d) => d.trim()).filter((d) => d !== "");
        if (!divIds.length) return true;
        return divIds.includes("0") || divIds.length > 1;
    }, [eventData?.event_user_div]);

    const divisionAttendanceStats = useMemo(() => {
        if (!isMultiOrAllDivisionEvent || !FullMembersList.length) return [];
        const map = {};
        FullMembersList.forEach((member) => {
            const divId = String(member.user_div || "0");
            if (!map[divId]) {
                map[divId] = {
                    div_id: divId,
                    division_name: member.division_name || "Unknown",
                    division_number: member.division_number || "",
                    ...emptyDivisionStat(),
                };
            }
            const bucket = getAttendanceBucket(member.response_status);
            map[divId][bucket] += 1;
            map[divId].total += 1;
        });
        return Object.values(map).sort((a, b) => {
            const numA = parseInt(a.division_number, 10) || 0;
            const numB = parseInt(b.division_number, 10) || 0;
            if (numA !== numB) return numA - numB;
            return String(a.division_name).localeCompare(String(b.division_name));
        });
    }, [FullMembersList, isMultiOrAllDivisionEvent]);

    useEffect(() => {
        const eventId = parseInt(location.state?.event_id, 10);
        if (!eventId || eventId < 1) {
            dispatch(setToastData({ message: "Event not found. Please open the event from the events list.", type: "error", showCloseBtn: true }));
            navigate('/events');
        }
        else {
            dispatch(setPageTitle('View Event'));
            getEventData(eventId);
        }
    }, [location.state?.event_id]);
    const getEventData = (eventId = location.state?.event_id) => {
        dispatch(setMainLoading(true));
        PostAxios(`event/view-event-by-id`, { event_id: eventId }, accessToken)
            .then(({ data }) => {
                if (String(data.status) === "1") {
                    let tempMemberList = [];
                    for (let i of data?.membersList) {
                        if (
                            (
                                userData.role_slug === "super_admin" ||
                                userData.role_slug === "org_admin") ||

                            (
                                (
                                    parseInt(data.data.event_admin) === parseInt(userData.id) ||
                                    parseInt(data.data.event_incharge) === parseInt(userData.id)
                                    // ||
                                    // userData.role_slug === "captain" ||
                                    // userData.role_slug === "div_admin"
                                )
                                && parseInt(userData.user_div) === parseInt(i.user_div)
                            )
                        ) {
                            tempMemberList.push(i);
                        }
                    }
                    setFullMembersList(data?.membersList || []);
                    setMemberList(tempMemberList);
                    if (data?.membersList.length > 0) {
                        let filteredData = data?.membersList.filter((item) => parseInt(item.user_id) === parseInt(userData.id) && item.response_status > 0);
                        if (filteredData.length > 0) {
                            setAlreadyMarked(true);
                            setmarkedTime(filteredData[0].response_on);
                        }
                        else {
                            setAlreadyMarked(false);
                            setmarkedTime('');
                        }
                    }
                    dispatch(setPageTitle(data.data.event_title));
                    setEventData(data.data);
                }
                else {
                    dispatch(setToastData({
                        message: (data.errors && Object.values(data.errors).length > 0)
                            ? ConvertErrorArrayToString(data.errors)
                            : (data.message || "Unable to load event details."),
                        isHtml: true,
                        type: "error",
                        showCloseBtn: true
                    }));
                    navigate('/events');
                }
            })
            .catch(err => {
                responseCatchFunc(dispatch, err);
            }).finally(() => {
                dispatch(setMainLoading(false));
            })
    }
    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert("Failed to open print window. Please check your browser's popup settings.");
            return;
        }

        // Convert base64 to blob URL
        const base64Image = eventData.event_qr;
        const byteCharacters = atob(base64Image);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });
        const blobURL = URL.createObjectURL(blob);
        printWindow.document.write(`<html>
        <head>
          <style>
            @media print {
            body, html {
                margin: 0;
                padding: 0;
                height: 100%;
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
            }
            body {
                display: flex;
                justify-content: center;
                align-items: center;
                flex-direction: column;
                height: 100vh;
                width: 100%;
            }
            div, h1 {
                width: 100%;
                display: block;
                text-align: center;
            }
            img {
                width: 80%;
                height: auto;
                display: block;
                margin: 0 auto; /* Center image horizontally */
            }
        }
          </style>
        </head>
        <body>
            <div>
                <img src="${blobURL}" />
            </div>
          <h1>${eventData.event_title}</h1>
        </body>
      </html>`);
        printWindow.document.close();

        printWindow.onload = () => {
            setTimeout(() => {
                try {
                    printWindow.print();
                } catch (error) {
                    console.error("Printing error:", error);
                    alert("Failed to print. Please try again.");
                }
                printWindow.close();
                URL.revokeObjectURL(blobURL); // Clean up the blob URL
            }, 500);
        };
    };
    const markAttendance = (attendanceStatus, userId, userDiv, qr_id) => {
        dispatch(setMainLoading(true));
        PostAxios(`event/mark-attendance`, {
            event_id: qr_id, //eventData.encryptedId,
            mem_id: userId,
            attendance_response: attendanceStatus,
            div_id: userDiv
        }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    dispatch(setToastData({ message: `${data.message} `, isHtml: true, type: 'success', showCloseBtn: true }))
                    getEventData();
                }
                else {
                    dispatch(setToastData({ message: (Object.values(data.errors).length > 0) ? ConvertErrorArrayToString(data.errors) : data.message, isHtml: true, type: 'error', showCloseBtn: true }))
                }
            })
            .catch((err) => {
                responseCatchFunc(dispatch, err);
            })
            .finally(() => {
                dispatch(setMainLoading(false));
            })
    }
    const revertAttendance = (userId) => {
        dispatch(setMainLoading(true));
        PostAxios(`event/revert-attendance`, {
            event_id: eventData.encryptedId,
            mem_id: userId,
        }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    dispatch(setToastData({ message: `${data.message} `, isHtml: true, type: 'success', showCloseBtn: true }))
                    getEventData();
                }
                else {
                    dispatch(setToastData({ message: (data.errors) ? ConvertErrorArrayToString(data.errors) : data.message, isHtml: true, type: 'error', showCloseBtn: true }))
                }
            })
            .catch((err) => {
                responseCatchFunc(dispatch, err);
            })
            .finally(() => {
                dispatch(setMainLoading(false));
            })
    }
    const exportReport = () => {
        dispatch(setMainLoading(true));
        let form_data = new FormData();
        form_data.append('event_id', eventData.ID);
        form_data.append('token', accessToken);
        if (userData.role_slug !== "super_admin" && userData.role_slug !== "org_admin") {
            form_data.append('div_id', userData.user_div);
        }
        fetch(`${process.env.REACT_APP_API_URL}event/export-event-attendance`, {
            method: 'POST',
            body: form_data,
            mode: 'cors',
        })
            .then(response => {
                // Get the blob from the response
                const blob = response.blob();
                // Get the headers from the response
                const headers = response.headers;
                return Promise.all([blob, headers]);
            })
            .then(([blob, headers]) => {
                const contentDisposition = headers.get('Content-Disposition');
                const contentType = (headers.get('Content-Type') || blob.type || '').toLowerCase();
                const isZip = contentType.includes('application/zip') || contentType.includes('application/x-zip');
                let fileName = `event-attendance-${moment().format('YYYY-MM-DD-HH:mm:ss')}.${isZip ? 'zip' : 'xlsx'}`;
                if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(contentDisposition);
                    if (matches != null && matches[1]) {
                        fileName = matches[1].replace(/['"]/g, '');
                    }
                }
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            })
            .catch(error => responseCatchFunc(dispatch, error))
            .finally(() => {
                dispatch(setMainLoading(false));
            })

    }
    if (typeof eventData.event_title !== "undefined") {
        if (eventData.event_mode === "division_registration") {
            return (
                <DivisionRegistrationEventView
                    eventId={location.state.event_id}
                    onRefresh={getEventData}
                    scrollToRegister={!!location.state?.scrollToRegister}
                />
            );
        }

        const eventEnd = moment(`${eventData.event_end_date} ${eventData.event_end_time}`, 'YYYY-MM-DD HH:mm');
        const isDuringEvent = moment().isBefore(eventEnd);

        return (
            <>
                <div className="main-form-sec">
                    <div className="form-sec">
                        {(eventData?.event_incharge_name) && <div className="w-50  p-3">Event Incharge : <b>{eventData.event_incharge_name}</b></div>}
                        {(eventData?.event_admin_name) && <div className="w-50 p-3">Event Admin : <b>{eventData.event_admin_name}</b></div>}
                        <div className="w-50 p-3">Event Location : <b>{eventData.event_location}</b></div>
                        <div className="w-50 p-3">Event Description : <b>{eventData.event_description}</b></div>
                        <div className="w-50 p-3">Event Start Date & Time : <b>{moment(`${eventData.event_start_date} ${eventData.event_start_time}`).format('DD, MMM YYYY hh:mm A')}</b></div>
                        <div className="w-50 p-3">Event End Date & Time : <b>{moment(`${eventData.event_end_date} ${eventData.event_end_time}`).format('DD, MMM YYYY hh:mm A')}</b></div>

                        <div className="px-2 my-3 w-100">
                            {
                                (parseInt(eventData.event_admin) === parseInt(userData.id) || parseInt(eventData.event_incharge) === parseInt(userData.id) ||
                                    userData.role_slug === "super_admin" || userData.role_slug === "org_admin") &&
                                <>
                                    {
                                        isDuringEvent &&

                                        <button type="button" className="btn btn-blue mb-2" onClick={() => {
                                            openQrPopup(true);
                                            dispatch(OpenModal())
                                        }}><i className="fas fa-qrcode"></i> &nbsp;View QR</button>
                                    }
                                    {
                                        (
                                            (userData.role_slug === "super_admin" || userData.role_slug === "org_admin") ||
                                            //(parseInt(eventData.event_user_div) !== 0 && (userData.role_slug === "captain" || userData.role_slug === "div_admin" || parseInt(eventData.event_admin) === parseInt(userData.id) || parseInt(eventData.event_incharge) === parseInt(userData.id))
                                            (parseInt(eventData.event_user_div) !== 0 && (parseInt(eventData.event_admin) === parseInt(userData.id) || parseInt(eventData.event_incharge) === parseInt(userData.id))
                                            )
                                        ) &&

                                        <button type="button" className="btn btn-blue ms-2 mb-2" onClick={() => {
                                            exportReport()
                                        }}><i className="fas fa-download"></i> &nbsp;Export Attendance Report</button>
                                    }
                                    <button type="button" className="btn btn-blue ms-2 mb-2" onClick={() => {
                                        getEventData();
                                    }}><i className="fas fa-sync"></i> &nbsp;Refresh List</button>

                                </>
                            }
                            {
                                ((moment().isBetween(
                                    moment(`${eventData.event_start_date} ${eventData.event_start_time}`, "YYYY-MM-DD HH:mm"),
                                    moment(`${eventData.event_end_date} ${eventData.event_end_time}`, "YYYY-MM-DD HH:mm"),
                                    'minute',
                                    '[]'
                                )) &&
                                    moment().isSameOrAfter(moment(`${moment().format("YYYY-MM-DD")} ${eventData.event_start_time}`)) &&
                                    moment().isSameOrBefore(moment(`${moment().format("YYYY-MM-DD")} ${eventData.attendence_time}`))) && <>
                                    {
                                        !alreadyMarked &&
                                        <button type="button" className="btn btn-blue ms-2 mb-2" onClick={() => {
                                            openQrScannerPopup(true);
                                            dispatch(OpenModal())
                                        }}><i className="fas fa-camera"></i> &nbsp;Scan QR Code</button>
                                    }
                                </>
                            }
                            {
                                alreadyMarked && <>
                                    <h3 className="text-success mt-2"><strong>Scanned On : {moment(markedTime).format('DD, MMM YYYY hh:mm A')}</strong></h3>
                                </>
                            }
                        </div>

                    </div>
                </div>
                {
                    (
                        userData.role_slug === "super_admin" ||
                        userData.role_slug === "org_admin" ||
                        parseInt(eventData.event_admin) === parseInt(userData.id) ||
                        parseInt(eventData.event_incharge) === parseInt(userData.id)
                    )
                    && isMultiOrAllDivisionEvent
                    && divisionAttendanceStats.length > 0
                    &&
                    <div className="main-form-sec mt-3">
                        <h4 className="mb-1 px-2">Division-wise Attendance</h4>
                        <p className="text-muted small px-2 mb-3">No response is counted as Absent.</p>
                        <div className="d-flex flex-wrap gap-3 pb-3">
                            {divisionAttendanceStats.map((div) => (
                                <div
                                    key={div.div_id}
                                    className="p-3 m-2"
                                    style={{
                                        minWidth: 240,
                                        flex: "1 1 240px",
                                        maxWidth: 320,
                                        background: "#f8f9fa",
                                        border: "1px solid #dee2e6",
                                        borderRadius: 6,
                                    }}
                                >
                                    <h5 className="mb-2" style={{ fontSize: 15 }}>
                                        {div.division_number ? `Div ${div.division_number} - ` : ""}{div.division_name}
                                    </h5>
                                    <div className="small text-muted mb-2">Total Members: <b>{div.total}</b></div>
                                    <div className="d-flex flex-column gap-1" style={{ fontSize: 13 }}>
                                        <div>Present: <b className="text-success">{div.present}</b></div>
                                        <div>Present (Late): <b style={{ color: "#b78105" }}>{div.late}</b></div>
                                        <div>Absent: <b className="text-danger">{div.absent}</b></div>
                                        <div>Out of Station: <b>{div.out_of_station}</b></div>
                                        <div>INT Khidmat: <b>{div.intl_khidmat}</b></div>
                                        <div>Leave: <b>{div.leave}</b></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                }
                {
                    (
                        userData.role_slug === "super_admin" ||
                        userData.role_slug === "org_admin" ||
                        parseInt(eventData.event_admin) === parseInt(userData.id) ||
                        parseInt(eventData.event_incharge) === parseInt(userData.id)
                        // ||
                        // userData.role_slug === "captain" ||
                        // userData.role_slug === "div_admin"
                    )
                    &&
                    <div className="main-form-sec mt-3">
                        <div className="form-sec d-flex flex-wrap gap-3 align-items-end ps-0 pe-3 mb-3">
                            <div className="form-field-group mb-0 flex-grow-1" style={{ minWidth: 260, maxWidth: 420 }}>
                                <label>Search Member</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Name, ITS, or division..."
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                />
                            </div>
                            {memberSearch.trim() && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-blue"
                                        style={{ background: "#6c757d", borderColor: "#6c757d" }}
                                        onClick={() => setMemberSearch("")}
                                    >
                                        Clear
                                    </button>
                                    <span className="text-muted align-self-center" style={{ fontSize: 14 }}>
                                        {filteredMembersList.length} member{filteredMembersList.length === 1 ? "" : "s"} found
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="table-wrapper">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>S.No.</th>
                                        <th>Member</th>
                                        {
                                            (userData.role_slug === "super_admin" ||
                                                userData.role_slug === "org_admin") &&
                                            <th>Division Name</th>
                                        }
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        filteredMembersList.length > 0 && filteredMembersList.map((item, index) => {
                                            return (
                                                <tr key={`ml-${item.user_id || index}`}>
                                                    <td>{index + 1}</td>
                                                    <td>{getMemberDisplayName(item)}</td>
                                                    {
                                                        (userData.role_slug === "super_admin" ||
                                                            userData.role_slug === "org_admin") &&
                                                        <td>{item.division_name}</td>
                                                    }
                                                    <td>
                                                        {
                                                            ((moment().isBetween(
                                                                moment(`${eventData.event_start_date} ${eventData.event_start_time}`, "YYYY-MM-DD HH:mm"),
                                                                moment(`${eventData.event_end_date} ${eventData.event_end_time}`, "YYYY-MM-DD HH:mm"),
                                                                'minute',
                                                                '[]'
                                                            )) &&
                                                                moment().isSameOrAfter(moment(`${moment().format("YYYY-MM-DD")} ${eventData.event_start_time}`)) &&
                                                                moment().isSameOrBefore(moment(`${moment().format("YYYY-MM-DD")} ${eventData.attendence_time}`))) && parseInt(item.response_status) === 0 &&
                                                            <>
                                                                <button className="btn btn-sm status-btn approved mb-1" onClick={() => {
                                                                    markAttendance(1, item.user_id, item.user_div, eventData.encryptedId);
                                                                }} title="Present">
                                                                    Present
                                                                </button>
                                                                <button className="status-btn rejected ms-1 mb-1" title="Absent" onClick={() => {
                                                                    markAttendance(3, item.user_id, item.user_div, eventData.encryptedId);
                                                                }}>
                                                                    Absent
                                                                </button>
                                                                <button className="btn btn-sm status-btn verification-pending ms-1 mb-1" title="INT Khidmat" onClick={() => {
                                                                    markAttendance(6, item.user_id, item.user_div, eventData.encryptedId);
                                                                }}>
                                                                    Leave
                                                                </button>
                                                                <button className="btn btn-sm btn-blue ms-1 mb-1" title="Out of Station" onClick={() => {
                                                                    markAttendance(4, item.user_id, item.user_div, eventData.encryptedId);
                                                                }}>
                                                                    Out of Station
                                                                </button>

                                                                <button className="btn btn-sm btn-blue ms-1 mb-1" title="INT Khidmat" onClick={() => {
                                                                    markAttendance(5, item.user_id, item.user_div, eventData.encryptedId);
                                                                }}>
                                                                    INT Khidmat
                                                                </button>
                                                            </>
                                                        }
                                                        {
                                                            parseInt(item.response_status) === 1 && <span className="status-text approved mb-2">
                                                                Present &nbsp;
                                                                {moment(item.response_on).format('DD, MMM Y hh:mm A')}
                                                            </span>
                                                        }
                                                        {
                                                            parseInt(item.response_status) === 2 && <span className="status-text verification-pending mb-2">
                                                                {`Present (Late)`} &nbsp;
                                                                {moment(item.response_on).format('DD, MMM Y hh:mm A')}
                                                            </span>
                                                        }
                                                        {
                                                            parseInt(item.response_status) === 3 && <span className="status-text rejected mb-2">
                                                                Absent &nbsp;
                                                                {moment(item.response_on).format('DD, MMM Y hh:mm A')}
                                                            </span>
                                                        }
                                                        {
                                                            parseInt(item.response_status) === 4 && <span className="status-text mb-2">
                                                                Out of station &nbsp;
                                                                {moment(item.response_on).format('DD, MMM Y hh:mm A')}
                                                            </span>
                                                        }
                                                        {
                                                            parseInt(item.response_status) === 5 && <span className="status-text mb-2">
                                                                INT Khidmat &nbsp;
                                                                {moment(item.response_on).format('DD, MMM Y hh:mm A')}
                                                            </span>
                                                        }
                                                        {
                                                            parseInt(item.response_status) === 6 && <span className="status-text mb-2">
                                                                Leave &nbsp;
                                                                {moment(item.response_on).format('DD, MMM Y hh:mm A')}
                                                            </span>
                                                        }
                                                        {
                                                            (parseInt(item.response_status) > 0 && moment(`${eventData.event_end_date} ${eventData.event_end_time}`) > moment()) && <>
                                                                &nbsp;<button className="btn btn-sm btn-blue ms-2 mt-2" onClick={() => {
                                                                    revertAttendance(item.user_id);
                                                                }}>Revert</button>
                                                            </>
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    }
                                    {
                                        membersList.length > 0 && filteredMembersList.length === 0 && (
                                            <tr>
                                                <td colSpan={(userData.role_slug === "super_admin" || userData.role_slug === "org_admin") ? 4 : 3} className="text-center text-muted py-4">
                                                    No members found for this search.
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
                {
                    qrPopup === true && <Modal classList="popup-main invoice-popup" style={{ maxWidth: 400, minHeight: 500, width: '100%' }} onClose={() => {
                        openQrPopup(false);
                    }}>
                        <h3 className="text-center">Scan this QR to mark attendance</h3>
                        <div className="my-2 text-center"><button type="button" className="btn btn-sm btn-blue" onClick={() => { handlePrint() }}>Print QR</button></div>
                        <img src={`data:image/png;base64,${eventData.event_qr}`} alt={`${eventData.event_title} Event QR Code`} />
                        <h3 className="text-center">{eventData.event_title}</h3>
                    </Modal>
                }
                {
                    qrScannerPopup === true && <Modal classList="popup-main invoice-popup" style={{ maxWidth: 400, minHeight: 500, width: '100%' }} onClose={() => {
                        openQrScannerPopup(false);
                    }}>
                        <h3 className="text-center">{eventData.event_title}</h3>
                        <Scanner onScan={(result) => {
                            dispatch(closeModal())
                            openQrScannerPopup(false);
                            markAttendance(1, userData.id, userData.user_div, result[0].rawValue);
                        }} />
                    </Modal>
                }
            </>
        )
    }
    return null;
}

export default ViewEvent;